import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArctionDashboard, arctionHelper } from '../../ArctionDashboard';
import { useAppSettings } from '../../Contexts/AppSettingsProvider';
import { AxisTickStrategies, ColorHEX, SolidFill, SolidLine, transparentLine } from '@arction/lcjs';

const CHART_HEIGHT = 450;

const DATA_USAGE_CHART_DARK = {
    cardBg: '#252525',
    border: 'rgba(255, 255, 255, 0.12)',
    legendDivider: 'rgba(255, 255, 255, 0.08)',
    text: 'rgba(255, 255, 255, 0.88)',
    textMuted: 'rgba(255, 255, 255, 0.38)',
    focusBg: 'rgba(0, 148, 194, 0.22)',
};

/** M/D/YYYY h:mm:ss AM/PM */
function formatAxisDateTime(ms) {
    const d = new Date(ms);
    const M = d.getMonth() + 1;
    const D = d.getDate();
    const Y = d.getFullYear();
    let h = d.getHours();
    const m = d.getMinutes();
    const s = d.getSeconds();
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${M}/${D}/${Y} ${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')} ${ampm}`;
}

const SERIES_COLORS = [
    '#ff3925',
    '#009513',
    '#ffa42b',
    '#9b644c',
    '#bf4588',
    '#e6cb00',
    '#0562c0',
    '#f5845a',
    '#4b8b03',
    '#a356a2',
];

/** Visible: new SolidLine. Hidden: transparentLine (LCJS docs — emptyLine is unreliable for LineSeries restore). */
function buildDataUsageLineStroke(colorHex) {
    return new SolidLine({
        thickness: 1,
        fillStyle: new SolidFill({ color: ColorHEX(colorHex) }),
    });
}

/** Y from chart props (not LCJS getYMin), so scale matches visible/isolated series reliably */
function collectDataUsageYValues(seriesData, isolatedSeriesIndex) {
    const ys = [];
    const pushFrom = (item) => {
        item?.points?.forEach((p) => {
            if (typeof p.y === 'number' && Number.isFinite(p.y)) ys.push(p.y);
        });
    };
    if (isolatedSeriesIndex != null && seriesData[isolatedSeriesIndex]) {
        pushFrom(seriesData[isolatedSeriesIndex]);
    } else {
        seriesData.forEach(pushFrom);
    }
    return ys;
}

/**
 * LCJS AxisScrollStrategies.expansion refits Y from all series data — hidden (transparent) lines still count,
 * so manual setInterval was almost immediately overridden. Use undefined scroll strategy to lock the range.
 */
function applyDataUsageYAxisFromSeriesData(axisY, seriesData, isolatedSeriesIndex) {
    if (!axisY || typeof axisY.setInterval !== 'function') return;

    const ys = collectDataUsageYValues(seriesData, isolatedSeriesIndex);
    if (!ys.length) return;

    let min = Math.min(...ys);
    let max = Math.max(...ys);
    const spanRaw = max - min;
    const span = spanRaw === 0 ? (Math.abs(min) > 1e-9 ? Math.abs(min) * 0.1 : 1) : spanRaw;

    let minPad = min - 0.1 * span - 0.005;
    let maxPad = max + 0.1 * span + 0.005;
    if (min >= 0) {
        minPad = Math.max(0, minPad);
    }

    try {
        if (typeof axisY.setScrollStrategy === 'function') {
            axisY.setScrollStrategy(undefined);
        }
        if (typeof axisY.stop === 'function') {
            axisY.stop();
        }
        axisY.setInterval(minPad, maxPad, 100);
    } catch (_) {
        /* ignore */
    }
}

function applyDataUsageSeriesStrokeVisibility(lineSeriesArr, seriesData, isolatedSeriesIndex) {
    if (!Array.isArray(lineSeriesArr) || !lineSeriesArr.length) return;
    seriesData.forEach((_, i) => {
        const line = lineSeriesArr[i];
        if (!line || typeof line.setStrokeStyle !== 'function') return;
        const show = isolatedSeriesIndex == null || i === isolatedSeriesIndex;
        try {
            if (show) {
                const color = SERIES_COLORS[i % SERIES_COLORS.length];
                line.setStrokeStyle(buildDataUsageLineStroke(color));
            } else {
                line.setStrokeStyle(transparentLine);
            }
        } catch (_) {
            /* ignore */
        }
    });
}

/**
 * Renders a time-series chart for Data Usage Settings (by-interval stats).
 * Same approach as Jobs page: ArctionDashboard with startTime = dateOrigin, data as offset (ms from origin).
 * Axis uses default DateTime strategy from createChart() so labels and count match Jobs.
 * @param {Object} props
 * @param {{ name: string, points: { x: number, y: number }[] }[]} props.series - One line per company (x = offset ms from dateOrigin)
 * @param {Date} props.dateOrigin - Start of time range (used as startTime for chart)
 * @param {{ min: number, max: number }} props.timeRange - Absolute ms (for X range when series empty)
 * @param {boolean} props.loading
 * @param {boolean} props.embedded - When true, do not wrap in pageCard (for single shared white container)
 * @param {number} [props.legendPaddingRight] - Extra px padding on legend (e.g. room for overlay controls)
 */
const DataUsageStatsChart = ({ series: seriesList = [], dateOrigin, timeRange, loading, embedded, legendPaddingRight = 0 }) => {
    const { appTheme: theme } = useAppSettings();
    const isDarkTheme = theme === 'Dark';
    const [chartId] = useState(() => 'data-usage-stats-chart-' + Math.random().toString(36).slice(2));
    const containerRef = useRef(null);
    const chartRef = useRef(null);
    const dashboardRef = useRef(null);
    const seriesRefsRef = useRef([]);
    /** Same Y axis passed to all line series (default axis is disposed in ArctionDashboard.createChart). */
    const yAxisRef = useRef(null);

    /** null = all lines; number = only series at that index (stable vs company name strings) */
    const [isolatedSeriesIndex, setIsolatedSeriesIndex] = useState(null);

    const hasData = seriesList.length > 0 && seriesList.some((s) => s.points?.length > 0);

    useEffect(() => {
        setIsolatedSeriesIndex(null);
    }, [seriesList.length]);

    useEffect(() => {
        if (!chartId || !containerRef.current) return;
        if (!dateOrigin && !timeRange) return;

        const originDate = dateOrigin ? new Date(dateOrigin) : new Date(timeRange.min);
        const arction = new ArctionDashboard({
            id: chartId,
            theme,
            height: CHART_HEIGHT,
            startTime: originDate,
            disableUserZoom: true,
        });

        const dashboard = arction.createDashboard();
        const chart = arction.createChart();
        chart.setPadding({ top: 12, left: 15, right: 24, bottom: 5 });
        const yAxis = arction.addYAxis({ opposite: false, title: '' });
        yAxisRef.current = yAxis;

        const lineSeries = [];
        seriesList.forEach((item, index) => {
            const color = SERIES_COLORS[index % SERIES_COLORS.length];
            const line = chart
                .addLineSeries({
                    dataPattern: { pattern: 'ProgressiveX' },
                    yAxis,
                })
                .setStrokeStyle(buildDataUsageLineStroke(color))
                .setName(item.name || '');
            lineSeries.push(line);
        });

        dashboardRef.current = dashboard;
        chartRef.current = chart;
        seriesRefsRef.current = lineSeries;

        seriesList.forEach((item, i) => {
            if (lineSeries[i] && item.points?.length) {
                const sorted = [...item.points].sort((a, b) => a.x - b.x);
                lineSeries[i].clear().add(sorted);
            }
        });

        if (hasData) {
            arctionHelper.setAxisIntervals(chart);
            applyDataUsageYAxisFromSeriesData(yAxis, seriesList, null);
        } else if (timeRange?.min != null && timeRange?.max != null) {
            const axisX = chart.getDefaultAxisX();
            const maxOffset = timeRange.max - timeRange.min;
            const padding = maxOffset * 0.03 || 1;
            axisX.setInterval(0, maxOffset + padding);
        }

        // Formatter receives absolute timestamp (library adds dateOrigin internally), so just format value
        const fmt = (value) => formatAxisDateTime(value);
        const axisX = chart.getDefaultAxisX();
        axisX.setTickStrategy(AxisTickStrategies.DateTime, (tickStrategy) => {
            let s = tickStrategy.setDateOrigin(originDate);
            if (s.setFormattingDay) s = s.setFormattingDay(fmt, fmt, fmt);
            if (s.setFormattingWeek) s = s.setFormattingWeek(fmt, fmt, fmt);
            if (s.setFormattingMonth) s = s.setFormattingMonth(fmt, fmt, fmt);
            if (s.setFormattingYear) s = s.setFormattingYear(fmt, fmt, fmt);
            s = s
                .setFormattingHour(fmt, fmt, fmt)
                .setFormattingMinute(fmt, fmt, fmt)
                .setFormattingSecond(fmt, fmt, fmt)
                .setFormattingMilliSecond(fmt, fmt)
                .setCursorFormatter(fmt);
            return s;
        });

        /** LCJS needs an explicit layout after mount; without it the canvas can stay blank when the container was 0×0 or just became visible (e.g. tab switch). Same as TimeBasedChart / UnitTimeBasedChart. */
        const layoutEngines = () => {
            try {
                dashboard.engine.layout();
                chart.engine.layout();
            } catch (_) {
                /* ignore */
            }
        };
        layoutEngines();
        const layoutRafId = requestAnimationFrame(() => {
            layoutEngines();
        });

        let resizeObserver;
        if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
            resizeObserver = new ResizeObserver(() => {
                layoutEngines();
            });
            resizeObserver.observe(containerRef.current);
        }

        return () => {
            cancelAnimationFrame(layoutRafId);
            resizeObserver?.disconnect();
            chart.dispose();
            dashboard.dispose();
            chartRef.current = null;
            dashboardRef.current = null;
            seriesRefsRef.current = [];
            yAxisRef.current = null;
        };
    }, [chartId, theme, dateOrigin, timeRange, seriesList.length, loading]);

    useLayoutEffect(() => {
        const chart = chartRef.current;
        const lineSeries = seriesRefsRef.current;
        if (!chart || !lineSeries.length) return;
        seriesList.forEach((item, i) => {
            if (!lineSeries[i]) return;
            if (!item.points?.length) {
                lineSeries[i].clear();
                return;
            }
            const sorted = [...item.points].sort((a, b) => a.x - b.x);
            lineSeries[i].clear().add(sorted);
        });
        applyDataUsageSeriesStrokeVisibility(lineSeries, seriesList, isolatedSeriesIndex);
        const yAxis = yAxisRef.current;
        if (hasData) {
            arctionHelper.setAxisIntervals(chart);
            if (yAxis) applyDataUsageYAxisFromSeriesData(yAxis, seriesList, isolatedSeriesIndex);
        } else if (timeRange?.min != null && timeRange?.max != null) {
            const axisX = chart.getDefaultAxisX();
            const maxOffset = timeRange.max - timeRange.min;
            const padding = maxOffset * 0.03 || 1;
            axisX.setInterval(0, maxOffset + padding);
        }
        let rafId = 0;
        if (hasData) {
            rafId = requestAnimationFrame(() => {
                const c = chartRef.current;
                const ls = seriesRefsRef.current;
                if (!c || !ls?.length) return;
                applyDataUsageSeriesStrokeVisibility(ls, seriesList, isolatedSeriesIndex);
                arctionHelper.setAxisIntervals(c);
                const ay = yAxisRef.current;
                if (ay) applyDataUsageYAxisFromSeriesData(ay, seriesList, isolatedSeriesIndex);
            });
        }
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [seriesList, hasData, timeRange, isolatedSeriesIndex]);

    const loadingEmptyStyle = {
        minHeight: CHART_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...(embedded
            ? {
                  border: `1px solid ${isDarkTheme ? DATA_USAGE_CHART_DARK.border : 'rgba(0,0,0,0.12)'}`,
                  borderRadius: 8,
                  ...(isDarkTheme ? { backgroundColor: DATA_USAGE_CHART_DARK.cardBg } : {}),
              }
            : { backgroundColor: '#fff' }),
    };
    const loadingWrapProps = embedded ? { style: loadingEmptyStyle } : { className: 'pageCard', style: loadingEmptyStyle };

    if (loading) {
        return (
            <div {...loadingWrapProps}>
                <span style={{ color: isDarkTheme ? DATA_USAGE_CHART_DARK.text : 'rgba(68, 68, 68, 0.88)' }}>Loading chart...</span>
            </div>
        );
    }

    if (!dateOrigin && !timeRange) {
        return (
            <div {...loadingWrapProps}>
                <span style={{ color: isDarkTheme ? 'rgba(255,255,255,0.56)' : 'rgb(144, 144, 144)' }}>No data for chart.</span>
            </div>
        );
    }

    const chartWrapStyle = {
        padding: 0,
        paddingTop: 10,
        boxSizing: 'border-box',
        ...(embedded
            ? {
                  border: `1px solid ${isDarkTheme ? DATA_USAGE_CHART_DARK.border : 'rgba(0,0,0,0.12)'}`,
                  borderRadius: 8,
                  ...(isDarkTheme ? { backgroundColor: DATA_USAGE_CHART_DARK.cardBg } : {}),
              }
            : { backgroundColor: '#fff' }),
    };
    const chartWrapProps = embedded ? { style: chartWrapStyle } : { className: 'pageCard', style: chartWrapStyle };

    const showHtmlLegend = seriesList.length > 0;

    return (
        <div {...chartWrapProps}>
            {showHtmlLegend && (
                <div
                    className="dataUsageStatsChartLegend"
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '6px 12px',
                        padding: '4px 8px 8px',
                        ...(legendPaddingRight > 0 ? { paddingRight: 8 + legendPaddingRight } : {}),
                        ...(embedded && isDarkTheme ? { backgroundColor: DATA_USAGE_CHART_DARK.cardBg } : {}),
                        borderBottom: embedded
                            ? `1px solid ${isDarkTheme ? DATA_USAGE_CHART_DARK.legendDivider : 'rgba(0,0,0,0.06)'}`
                            : undefined,
                    }}
                >
                    {seriesList.map((item, index) => {
                        const name = item.name || `Series ${index}`;
                        const color = SERIES_COLORS[index % SERIES_COLORS.length];
                        const isFocused = isolatedSeriesIndex === index;
                        const dimmed = isolatedSeriesIndex !== null && !isFocused;
                        return (
                            <button
                                key={`${name}-${index}`}
                                type="button"
                                onClick={() => {
                                    setIsolatedSeriesIndex((prev) => {
                                        if (prev === null) return index;
                                        if (prev === index) return null;
                                        return index;
                                    });
                                }}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '4px 8px',
                                    margin: 0,
                                    border: 'none',
                                    borderRadius: 16,
                                    background: isFocused
                                        ? isDarkTheme
                                            ? DATA_USAGE_CHART_DARK.focusBg
                                            : 'rgba(0, 148, 194, 0.12)'
                                        : 'transparent',
                                    cursor: 'pointer',
                                    fontSize: 13,
                                    fontWeight: isFocused ? 600 : 400,
                                    color: dimmed
                                        ? isDarkTheme
                                            ? DATA_USAGE_CHART_DARK.textMuted
                                            : 'rgba(0,0,0,0.38)'
                                        : isDarkTheme
                                          ? DATA_USAGE_CHART_DARK.text
                                          : 'rgba(0,0,0,0.87)',
                                    fontFamily: 'inherit',
                                }}
                            >
                                <span
                                    aria-hidden
                                    style={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        backgroundColor: color,
                                        flexShrink: 0,
                                        opacity: dimmed ? 0.35 : 1,
                                    }}
                                />
                                {name}
                            </button>
                        );
                    })}
                </div>
            )}
            <div
                ref={containerRef}
                id={chartId}
                style={{ width: '100%', height: CHART_HEIGHT }}
            />
        </div>
    );
};

export default DataUsageStatsChart;
